import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Heart, Home, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const ThankYou = () => {
  useEffect(() => {
    // Fire GA4 donation_completed event when page loads
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'donation_completed', {
        event_category: 'fundraising',
        event_label: 'stripe_success'
      });
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      {/* Hero Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            {/* Success Icon */}
            <div className="relative inline-block mb-8">
              <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-14 h-14 text-emerald-600" />
              </div>
              <div className="absolute -top-2 -right-2 w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
            </div>

            {/* Thank You Message */}
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Thank You!
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Your generous donation has been received. You're helping us empower communities 
              and create lasting change.
            </p>

            {/* Confirmation Card */}
            <Card className="border-2 border-emerald-200 shadow-lg mb-8">
              <CardContent className="p-8">
                <div className="flex items-center justify-center mb-4">
                  <span className="inline-block px-4 py-1 bg-emerald-100 text-emerald-700 text-sm font-semibold rounded-full">
                    Donation Successful
                  </span>
                </div>
                <p className="text-gray-700 mb-6">
                  A confirmation email will be sent to you shortly. Your support directly funds 
                  youth empowerment, skills training, and sustainable development initiatives 
                  in communities that need it most.
                </p>
                <div className="border-t border-gray-200 pt-6">
                  <p className="text-sm text-gray-500">
                    If you have any questions about your donation, please contact us at{' '}
                    <a 
                      href="mailto:info@delinamfoundation.org" 
                      className="text-emerald-700 hover:text-emerald-800 font-medium"
                    >
                      info@delinamfoundation.org
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button 
                  size="lg" 
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8"
                >
                  <Home className="mr-2 w-5 h-5" />
                  Back to Home
                </Button>
              </Link>
              <Link to="/get-involved">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 font-semibold px-8"
                >
                  More Ways to Help
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Your Impact Matters
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 bg-emerald-50 rounded-lg">
                <div className="text-3xl font-bold text-emerald-600 mb-2">100%</div>
                <p className="text-gray-600 text-sm">
                  of donations go directly to our programs
                </p>
              </div>
              <div className="p-6 bg-emerald-50 rounded-lg">
                <div className="text-3xl font-bold text-emerald-600 mb-2">500+</div>
                <p className="text-gray-600 text-sm">
                  community members supported
                </p>
              </div>
              <div className="p-6 bg-emerald-50 rounded-lg">
                <div className="text-3xl font-bold text-emerald-600 mb-2">10+</div>
                <p className="text-gray-600 text-sm">
                  active community programs
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Share Section */}
      <section className="py-12 bg-gradient-to-br from-emerald-600 to-emerald-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Help Us Spread the Word
          </h2>
          <p className="text-emerald-50 mb-6 max-w-xl mx-auto">
            Share your support with friends and family. Together, we can reach more 
            communities and create even greater impact.
          </p>
          <div className="flex justify-center gap-4">
            <a
              href="https://twitter.com/intent/tweet?text=I%20just%20donated%20to%20Delinam%20Foundation%20to%20help%20empower%20communities!%20Join%20me%20in%20making%20a%20difference.&url=https://delinamfoundation.org"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-white text-emerald-700 font-semibold rounded-lg hover:bg-emerald-50 transition-colors"
            >
              Share on Twitter
            </a>
            <a
              href="https://www.facebook.com/sharer/sharer.php?u=https://delinamfoundation.org"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-white text-emerald-700 font-semibold rounded-lg hover:bg-emerald-50 transition-colors"
            >
              Share on Facebook
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ThankYou;
